import type React from "react";
import { type ChangeEvent, useState } from "react";
import { type IEmailInputProps } from "@msidentity/SISU/components/inputs/email-input";
import { PhoneNumberFormat } from "@msidentity/SISU/components/inputs/input-regex-constants";
import { type PhoneNumberFabricProps } from "@msidentity/SISU/components/inputs/phone-number/fabric/phone-number-fabric";
import { type TextButtonFabricProps } from "@msidentity/SISU/components/text-button/fabric/text-button-fabric";
import { type OnSwitchMemberNameType } from "@msidentity/SISU/components/username-collection/username-collection-types";
import { type ViewFormProps } from "@msidentity/SISU/components/view-container/view-form-container";
import { ViewId } from "@msidentity/SISU/constants";
import { useEffectAfterInitialRender } from "@msidentity/SISU/hooks/use-effect-after-initial-render";
import { useRedirect } from "@msidentity/SISU/hooks/use-redirect";
import { MemberNameType } from "@msidentity/SISU/model/user";
import {
  usernameRecoveryCollectProofViewStringsFabric,
  usernameRecoveryGenericStringsFabric as strings,
} from "../../../../fabric/username-recovery-strings-fabric";
import { useSendOtt } from "../../../../model/hooks/use-send-ott";
import UsernameRecoveryConfig from "../../../../username-recovery-config";
import { useUsernameRecoveryContext } from "../../../../username-recovery-context";
import { validateUsernameInputAndDispatchError } from "../../../../username-recovery-helpers";
import { UsernameRecoveryActionType } from "../../../../username-recovery-reducer";
import { type UsernameRecoveryMemberNamePrefill } from "../../../../username-recovery-types";

export type CollectProofViewContainerProps = {
  applyAriaLive: boolean;
  title: {
    title: string;
  };
  description: {
    description: string;
  };
  secondaryButton: Pick<TextButtonFabricProps, "label" | "type" | "onClick">;
  primaryButton: Pick<TextButtonFabricProps, "label" | "type" | "onClick">;
  form: Pick<ViewFormProps, "dataTestId" | "name" | "onSubmit">;
};

export type CollectProofViewState = {
  inputVal: string;
  inputType: UsernameRecoveryMemberNamePrefill;
  easiInputProps: IEmailInputProps;
  phoneInputProps: PhoneNumberFabricProps;
  viewProps: CollectProofViewContainerProps;
  setInputType: OnSwitchMemberNameType;
  onInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
};

export const useCollectProofView = (): CollectProofViewState => {
  const { phoneCountries, cancelUrl } = UsernameRecoveryConfig.instance;
  const {
    viewState: { usernameVal, usernameType },
  } = useUsernameRecoveryContext();

  const {
    dispatchStateChange,
    viewState: { errorState, phoneCountryState },
  } = useUsernameRecoveryContext();

  const [inputVal, setInputVal] = useState(usernameVal);
  // React state which toggles between collecting email or phone
  const [inputType, setInputType] = useState(usernameType);
  // If the view is loaded with an error, we are ready to show errors
  const [readyForErrors, setReadyForErrors] = useState(!!errorState);

  const [phoneCountry, setPhoneCountry] = useState(phoneCountryState);
  const [shouldSwitchToPhone, setShouldSwitchToPhone] = useState(false);

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = String(event.target.value);
    setInputVal(newValue);
    if (
      inputType === MemberNameType.EASI &&
      newValue?.length &&
      new RegExp(PhoneNumberFormat).test(newValue)
    ) {
      setShouldSwitchToPhone(true);
    }
  };

  const onSwitchInputType = (type: MemberNameType) => {
    // We only support EASI or Phone
    if (type !== MemberNameType.EASI && type !== MemberNameType.Phone) {
      return;
    }

    dispatchStateChange({
      type: UsernameRecoveryActionType.SetError,
      error: "",
    });
    setInputType(type);
    setReadyForErrors(false);

    // If the user clicks "Use email instead" we should clear the input value
    if (type === MemberNameType.EASI) {
      setInputVal("");
      setShouldSwitchToPhone(false);
    }
  };

  const onInputBlur = () => {
    let newInputType = inputType;
    // If we're on EASI and the input is a phone number, switch to phone
    if (inputType === MemberNameType.EASI && shouldSwitchToPhone) {
      newInputType = MemberNameType.Phone;
      onSwitchInputType(newInputType);
    }
  };

  useEffectAfterInitialRender(() => {
    if (readyForErrors) {
      validateUsernameInputAndDispatchError({
        dispatchAction: dispatchStateChange,
        inputType,
        usernameValue: inputVal,
        strings: usernameRecoveryCollectProofViewStringsFabric,
      });
    }
  }, [readyForErrors, inputVal, inputType, dispatchStateChange]);

  const callSendOtt = useSendOtt({
    viewId: ViewId.UsernameRecoveryCollect,
    memberNameType: inputType,
    proofId: inputVal,
    phoneCountry,
  });

  const onSubmitHandler = async (event: React.SyntheticEvent) => {
    event.preventDefault();
    setReadyForErrors(true);

    if (shouldSwitchToPhone) {
      setShouldSwitchToPhone(false);
      return;
    }

    const isValidInput = validateUsernameInputAndDispatchError({
      dispatchAction: dispatchStateChange,
      inputType,
      usernameValue: inputVal,
      strings: usernameRecoveryCollectProofViewStringsFabric,
    });

    if (!isValidInput) {
      return;
    }

    // Update the context before calling the API so we have the values for success and failure
    if (inputType === MemberNameType.Phone) {
      dispatchStateChange({
        type: UsernameRecoveryActionType.SetUsernameProof,
        username: inputVal,
        usernameType: inputType,
        phoneCountry,
      });
    } else {
      dispatchStateChange({
        type: UsernameRecoveryActionType.SetUsernameProof,
        username: inputVal,
        usernameType: inputType,
      });
    }

    await callSendOtt();
  };

  const redirect = useRedirect();

  const onCancelHandler = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (cancelUrl) {
      redirect(cancelUrl);
    }
  };

  return {
    setInputType: onSwitchInputType,
    inputType,
    inputVal,
    onInputChange,
    easiInputProps: {
      name: "proof",
      placeholder: usernameRecoveryCollectProofViewStringsFabric.proofInputHintText,
      ariaLabel: usernameRecoveryCollectProofViewStringsFabric.enterProofLabel,
      type: "email",
      value: inputVal,
      onChange: onInputChange,
      onBlur: onInputBlur,
      showErrorInline: errorState !== "",
      externalError: errorState,
      maxLength: 113,
      hasInitialFocus: true,
    },
    phoneInputProps: {
      dropdownProps: {
        useInlinePhoneNumber: true,
        value: phoneCountry,
        countryData: phoneCountries,
        onChange: (country) => {
          setPhoneCountry(country);
        },
      },
      inputProps: {
        name: "proof",
        type: "tel",
        placeholder: usernameRecoveryCollectProofViewStringsFabric.phoneInputHintText,
        value: inputVal,
        onChange: onInputChange,
        hasFocus: true,
        onBlur: onInputBlur,
      },
      showErrorMessage: errorState !== "",
      errorMessage: errorState,
    },
    viewProps: {
      applyAriaLive: true,
      title: {
        title: strings.title,
      },
      description: {
        description: usernameRecoveryCollectProofViewStringsFabric.enterProofDescription,
      },
      secondaryButton: {
        label: strings.cancel,
        type: "button",
        onClick: onCancelHandler,
      },
      primaryButton: {
        label: strings.next,
        type: "submit",
      },
      form: {
        dataTestId: "fAccrualForm",
        name: "UsernameRecoveryForm",
        onSubmit: onSubmitHandler,
      },
    },
  };
};
